import { useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "@/core/api/crmApi";
import { exportApi } from "@/core/api/exportApi";
import { useAuthStore } from "@/core/auth/authStore";
import { hasPermission } from "@/core/auth/permissions";
import type { Account, CreateAccountRequest } from "@/core/types/crm";
import { PageHeader } from "@/shared/components/PageHeader";
import { formatDateTime } from "@/core/utils/date";
import { useSearchParams } from "react-router";

export const AccountsPage = (): React.ReactElement => {
  const { message } = App.useApp();
  const client = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = useAuthStore((state) => state.permissions);
  const [exporting, setExporting] = useState(false);
  const search = searchParams.get("search")?.trim() || "";
  const [searchInput, setSearchInput] = useState(search);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<CreateAccountRequest>();
  const query = useQuery({
    queryKey: ["accounts", search],
    queryFn: () => crmApi.accounts({ search, pageSize: 50 }),
  });
  useEffect(() => setSearchInput(search), [search]);
  const applySearch = (value: string): void => {
    const next = new URLSearchParams(searchParams);
    const term = value.trim();
    if (term) next.set("search", term);
    else next.delete("search");
    setSearchParams(next, { replace: true });
  };
  const create = useMutation({
    mutationFn: crmApi.createAccount,
    onSuccess: () => {
      message.success("سازمان ثبت شد.");
      setOpen(false);
      form.resetFields();
      void client.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: () => message.error("ثبت سازمان ناموفق بود."),
  });
  return (
    <div className="page-stack">
      <PageHeader
        title="سازمان‌ها"
        description="حساب‌های سازمانی، صنعت و شناسه ثبتی"
        extra={
          <Space>
            <>
              {hasPermission(permissions, "tenancy.manage") ? (
                <Button
                  icon={<DownloadOutlined />}
                  loading={exporting}
                  onClick={async () => {
                    setExporting(true);
                    try {
                      await exportApi.accounts();
                      message.success("فایل Excel مشتریان آماده شد.");
                    } catch {
                      message.error("دریافت فایل Excel مشتریان ناموفق بود.");
                    } finally {
                      setExporting(false);
                    }
                  }}
                >
                  خروجی Excel مشتریان
                </Button>
              ) : null}
            </>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpen(true)}
            >
              سازمان جدید
            </Button>
          </Space>
        }
      />
      <Card variant="borderless">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="نام یا شناسه سازمانی"
          onChange={(event) => {
            setSearchInput(event.target.value);
            if (!event.target.value) applySearch("");
          }}
          onPressEnter={(event) => applySearch(event.currentTarget.value)}
          style={{ maxWidth: 420 }}
          value={searchInput}
        />
      </Card>
      <Card variant="borderless">
        <Table<Account>
          rowKey="id"
          loading={query.isLoading}
          dataSource={query.data?.items}
          pagination={false}
          scroll={{ x: "max-content" }}
          columns={[
            {
              title: "نام سازمان",
              dataIndex: "name",
              render: (v) => <Typography.Text strong>{v}</Typography.Text>,
            },
            { title: "صنعت", dataIndex: "industry", render: (v) => v || "—" },
            {
              title: "شناسه سازمانی",
              dataIndex: "organizationIdentifier",
              render: (v) => <span dir="ltr">{v || "—"}</span>,
            },
            {
              title: "تاریخ ثبت",
              dataIndex: "createdAtUtc",
              render: formatDateTime,
            },
          ]}
          locale={{ emptyText: "سازمانی ثبت نشده است." }}
        />
      </Card>
      <Modal
        open={open}
        title="ثبت سازمان"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
          <Form.Item
            name="name"
            label="نام سازمان"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="industry" label="صنعت">
            <Input />
          </Form.Item>
          <Form.Item name="organizationIdentifier" label="شناسه سازمانی">
            <Input dir="ltr" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
